import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Calendar, MapPin, Users, User, Star, Heart, Settings, Camera,
  LogOut, Bell, Clock, Check, X, MessageSquare, FileText, Search, CreditCard,
  UtensilsCrossed, ShoppingCart, Plus, Minus, Car, HelpCircle, Gift
} from 'lucide-react';
// GuestServices removed — Food and Taxi are now direct sub-tabs under Services
import FAQFeedback from '../components/FAQFeedback';
import MessagingPanel from '../components/MessagingPanel';
import GuestBrowse from '../components/GuestBrowse';
import RoleSwitcher from '../components/RoleSwitcher';
import RideBooking from '../components/RideBooking';
import FoodOrdering from '../components/FoodOrdering';
import ServiceMessaging from '../components/ServiceMessaging';
import ReferralDashboard from '../components/ReferralDashboard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { bookingAPI, messagesAPI, paymentsAPI, restaurantAPI, driverAPI, reviewsAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { generateInvoicePDF } from '../utils/invoiceGenerator';

const GuestDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, getAuthToken, logout, isAuthenticated, loading: authLoading } = useAuth();

  // Tab state is derived from the URL — single source of truth for browser back/forward
  const activeTab = searchParams.get('tab') || 'browse';

  // When user clicks a tab, push a new URL (creates browser history entry)
  const setActiveTab = useCallback((tab) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);
  const [servicesSubTab, setServicesSubTab] = useState('food');
  const [openConversationId, setOpenConversationId] = useState(null);
  const [openConversationData, setOpenConversationData] = useState(null);
  const [bookings, setBookings] = useState([]);

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewedBookings, setReviewedBookings] = useState(new Set());
  const [justReviewedBookings, setJustReviewedBookings] = useState(new Set()); // Reviews submitted THIS session
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  // "Acknowledged" tracking so the red badge clears as soon as the user
  // opens the Messages or Services tab, and only comes back when a NEW
  // message arrives (poll detects count going up). Without this the badge
  // stayed visible indefinitely while the backend continued reporting the
  // same unread count, which read as a static permanent notification.
  const [messagesAck, setMessagesAck] = useState(false);
  const [servicesAck, setServicesAck] = useState(false);
  const prevUnreadRef = useRef(0);
  const prevSvcUnreadRef = useRef(0);
  const [unreadServiceMessages, setUnreadServiceMessages] = useState(0);

  // Profile photo state
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);

  // Food ordering state
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [foodOrders, setFoodOrders] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  
  // Taxi booking state
  const [rideRequests, setRideRequests] = useState([]);
  const [rideForm, setRideForm] = useState({
    pickupAddress: '',
    dropoffAddress: '',
    pickupTime: 'ASAP',
    notes: ''
  });

  // Fetch unread count for real-time badge updates
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const data = await messagesAPI.getUnreadCount(token);
        setUnreadMessages(data.guestUnread || data.unread || 0);
        setUnreadServiceMessages(data.serviceUnread || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [getAuthToken]);

  const handleUnreadChange = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const data = await messagesAPI.getUnreadCount(token);
        const newCount = data.guestUnread || data.unread || 0;
        const newSvcCount = data.serviceUnread || 0;
        if (newCount > prevUnreadRef.current && activeTab !== 'messages') {
          toast({ title: '💬 New message', description: 'You have a new message.' });
          setMessagesAck(false); // a genuinely new message arrived, re-show badge
        }
        if (newSvcCount > 0 && newSvcCount > (prevSvcUnreadRef.current || 0) && activeTab !== 'services') {
          setServicesAck(false); // new service message, re-show services badge
        }
        prevUnreadRef.current = newCount;
        prevSvcUnreadRef.current = newSvcCount;
        setUnreadMessages(newCount);
        setUnreadServiceMessages(newSvcCount);
      }
    } catch {}
  }, [getAuthToken, activeTab, toast]);

  // Listen for switchToServiceMessages event (from FoodOrdering "Message Restaurant" button)
  useEffect(() => {
    const handleSwitchToServiceMessages = (e) => {
      setActiveTab('services');
      setServicesSubTab('service-messages');
      // Pass the conversation ID so ServiceMessaging auto-opens it
      if (e.detail?.conversationId) {
        setOpenConversationId(e.detail.conversationId);
        setOpenConversationData(e.detail.conversation || null);
      }
    };
    window.addEventListener('switchToServiceMessages', handleSwitchToServiceMessages);
    return () => window.removeEventListener('switchToServiceMessages', handleSwitchToServiceMessages);
  }, [setActiveTab]);

  // Redirect if not authenticated — wait for auth to finish loading first
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated()) {
      fetchData();
    }
  }, [user]);

  // Poll for unread message counts every 8 seconds to show notification badges
  useEffect(() => {
    if (!isAuthenticated()) return;
    const poll = setInterval(fetchUnreadCount, 8000);
    return () => clearInterval(poll);
  }, [isAuthenticated, fetchUnreadCount]);

  // Acknowledge the unread badge as soon as the user opens the relevant tab.
  // The optimistic local clear (setUnreadMessages(0)) makes the red bubble
  // disappear immediately without waiting for the 8 second backend poll.
  // The ack state stays true until a NEW message arrives (poll detects
  // count going up), at which point handleUnreadChange flips it back to
  // false and the badge reappears.
  useEffect(() => {
    if (activeTab === 'messages') {
      setMessagesAck(true);
      setUnreadMessages(0);
      prevUnreadRef.current = 0;
    }
    if (activeTab === 'services') {
      setServicesAck(true);
      setUnreadServiceMessages(0);
      prevSvcUnreadRef.current = 0;
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const [bookingsData, unreadData, myReviews] = await Promise.all([
        bookingAPI.getGuestBookings(token),
        messagesAPI.getUnreadCount(token),
        reviewsAPI.getMyReviews(token).catch(() => [])
      ]);
      setBookings(bookingsData);
      setUnreadMessages(unreadData.unread || 0);

      // Pre-populate reviewedBookings from existing reviews
      if (myReviews && myReviews.length > 0) {
        const reviewedIds = new Set(myReviews.map(r => r.bookingId).filter(Boolean));
        setReviewedBookings(reviewedIds);
      }

      // Initialize profile photo from user data
      if (user?.image) setProfilePhotoUrl(user.image);

      // Set default delivery address from first confirmed booking
      const confirmedBooking = bookingsData.find(b => b.status === 'confirmed');
      if (confirmedBooking && confirmedBooking.propertyLocation) {
        setDeliveryAddress(confirmedBooking.propertyLocation);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRestaurants = useCallback(async (city = '') => {
    try {
      const token = getAuthToken();
      const data = await restaurantAPI.browseRestaurants(city, token);
      setRestaurants(data);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    }
  }, [getAuthToken]);
  
  const fetchRestaurantMenu = async (restaurantId) => {
    try {
      const token = getAuthToken();
      const data = await restaurantAPI.getRestaurantMenu(restaurantId, token);
      setSelectedRestaurant(data.restaurant);
      setMenuItems(data.menuItems);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast({ title: 'Error', description: 'Failed to load menu', variant: 'destructive' });
    }
  };
  
  const fetchFoodOrders = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await restaurantAPI.getMyOrders(token);
      setFoodOrders(data);
    } catch (error) {
      console.error('Failed to fetch food orders:', error);
    }
  }, [getAuthToken]);
  
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.menuItemId === item._id);
      if (existing) {
        return prev.map(i => i.menuItemId === item._id ? {...i, quantity: i.quantity + 1} : i);
      }
      return [...prev, { menuItemId: item._id, name: item.name, price: item.price, quantity: 1 }];
    });
  };
  
  const removeFromCart = (menuItemId) => {
    setCart(prev => {
      const existing = prev.find(i => i.menuItemId === menuItemId);
      if (existing && existing.quantity > 1) {
        return prev.map(i => i.menuItemId === menuItemId ? {...i, quantity: i.quantity - 1} : i);
      }
      return prev.filter(i => i.menuItemId !== menuItemId);
    });
  };
  
  const placeOrder = async () => {
    if (cart.length === 0) {
      toast({ title: 'Cart Empty', description: 'Add items to cart first', variant: 'destructive' });
      return;
    }
    if (!deliveryAddress) {
      toast({ title: 'Address Required', description: 'Please enter a delivery address', variant: 'destructive' });
      return;
    }
    
    try {
      const token = getAuthToken();
      const confirmedBooking = bookings.find(b => b.status === 'confirmed');
      
      await restaurantAPI.placeOrder({
        restaurantId: selectedRestaurant._id,
        items: cart,
        bookingId: confirmedBooking?._id || null,
        deliveryAddress,
        notes: orderNotes
      }, token);
      
      toast({ title: 'Order Placed!', description: 'Your food order has been placed' });
      setCart([]);
      setOrderNotes('');
      setSelectedRestaurant(null);
      setMenuItems([]);
      fetchFoodOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to place order', variant: 'destructive' });
    }
  };
  
  const cancelFoodOrder = async (orderId) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      const token = getAuthToken();
      await restaurantAPI.cancelOrder(orderId, token);
      toast({ title: 'Order Cancelled' });
      fetchFoodOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Cannot cancel order', variant: 'destructive' });
    }
  };
  
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  // No VAT added — platform is not VAT-registered; menu prices are gross
  const cartTotalWithVAT = cartTotal;
  
  // Taxi functions
  const fetchRideRequests = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await driverAPI.getMyRideRequests(token);
      setRideRequests(data);
    } catch (error) {
      console.error('Failed to fetch ride requests:', error);
    }
  }, [getAuthToken]);
  
  const requestRide = async () => {
    if (!rideForm.pickupAddress || !rideForm.dropoffAddress) {
      toast({ title: 'Error', description: 'Pickup and dropoff addresses are required', variant: 'destructive' });
      return;
    }
    
    try {
      const token = getAuthToken();
      const confirmedBooking = bookings.find(b => b.status === 'confirmed');
      
      await driverAPI.requestRide({
        pickupAddress: rideForm.pickupAddress,
        dropoffAddress: rideForm.dropoffAddress,
        pickupTime: rideForm.pickupTime,
        bookingId: confirmedBooking?._id || null,
        notes: rideForm.notes
      }, token);
      
      toast({ title: 'Ride Requested!', description: 'Drivers in the area have been notified' });
      setRideForm({ pickupAddress: '', dropoffAddress: '', pickupTime: 'ASAP', notes: '' });
      fetchRideRequests();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to request ride', variant: 'destructive' });
    }
  };
  
  const cancelRide = async (rideId) => {
    if (!window.confirm('Cancel this ride request?')) return;
    try {
      const token = getAuthToken();
      await driverAPI.cancelRideRequest(rideId, token);
      toast({ title: 'Ride Cancelled' });
      fetchRideRequests();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Cannot cancel ride', variant: 'destructive' });
    }
  };
  
  const getRideStatusBadge = (status) => {
    const styles = {
      requested: 'bg-amber-100 text-amber-800',
      accepted: 'bg-blue-100 text-blue-800',
      en_route: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  // Cancellation preview modal state. We pre-fetch the policy breakdown
  // when the user clicks Cancel on a *paid* booking so they see the
  // exact refund amount before committing. The pre-payment path doesn't
  // use this — it's the older simple cancel-the-stripe-session flow.
  const [cancelPreview, setCancelPreview] = useState(null); // { booking, preview }
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const handleCancelBooking = async (bookingId, isPendingPayment = false) => {
    try {
      const token = getAuthToken();

      if (isPendingPayment) {
        // Pre-payment cancellation. Nothing to refund; just expire the
        // Stripe session and release the calendar hold.
        if (!window.confirm('Are you sure you want to cancel this booking?')) return;
        await paymentsAPI.cancelPendingBooking(bookingId, token);
        toast({ title: 'Booking Cancelled', description: 'Your booking has been cancelled' });
        fetchData();
        return;
      }

      // Paid booking. Fetch the preview and open the modal — the guest
      // can see the exact refund amount and read the policy summary
      // before confirming.
      const preview = await paymentsAPI.getRefundPreview(bookingId, token);
      const booking = bookings.find(b => b._id === bookingId);
      setCancelPreview({ booking, preview });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to load cancellation preview';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    }
  };

  const submitCancellation = async () => {
    if (!cancelPreview) return;
    const { booking, preview } = cancelPreview;
    const refund = Number(preview?.breakdown?.guest_refund || 0);
    setCancelSubmitting(true);
    try {
      const token = getAuthToken();
      const result = await paymentsAPI.guestCancelWithRefund(booking._id, refund, token);
      setCancelPreview(null);
      if (result.alreadyProcessed) {
        toast({
          title: 'Already cancelled',
          description: 'A refund has already been processed for this booking.',
        });
      } else {
        toast({
          title: 'Booking cancelled',
          description:
            refund > 0
              ? `£${refund.toFixed(2)} refund is on its way (usually clears in 5-10 days).`
              : "Cancellation confirmed. No refund applied under the host's policy.",
        });
      }
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to cancel booking';
      if (error.response?.status === 409) {
        // Threshold crossed while the modal was open. Re-fetch the preview
        // so the guest sees the new number before committing again.
        try {
          const fresh = await paymentsAPI.getRefundPreview(booking._id, getAuthToken());
          setCancelPreview({ booking, preview: fresh });
          toast({
            title: 'Refund amount has changed',
            description: 'The refund window moved while you were here. Please review the new amount.',
            variant: 'destructive',
          });
        } catch {
          setCancelPreview(null);
          fetchData();
        }
      } else {
        setCancelPreview(null);
        toast({ title: 'Error', description: detail, variant: 'destructive' });
      }
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Profile photo upload handler
  const handleProfilePhotoUpload = async (file) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({ title: 'File too large', description: 'Profile photo must be under 5MB', variant: 'destructive' });
      return;
    }
    setUploadingProfilePhoto(true);
    try {
      const token = getAuthToken();
      const result = await userAPI.uploadProfilePhoto(file, token);
      setProfilePhotoUrl(result.url);
      toast({ title: 'Profile photo updated', description: 'Your profile picture has been saved.' });
    } catch (err) {
      console.error('Profile photo upload error:', err);
      toast({ title: 'Upload failed', description: 'Could not upload profile photo. Please try again.', variant: 'destructive' });
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  // Review handlers
  const openReviewModal = async (booking) => {
    // Check if user can review
    try {
      const token = getAuthToken();
      const result = await reviewsAPI.canReview(booking._id, token);
      if (!result.canReview) {
        toast({ title: 'Cannot Review', description: result.reason, variant: 'destructive' });
        return;
      }
    } catch (err) {
      // If API fails, still let them try
    }
    setReviewBooking(booking);
    setReviewRating(5);
    setReviewTitle('');
    setReviewComment('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      toast({ title: 'Error', description: 'Please write a review comment', variant: 'destructive' });
      return;
    }
    setSubmittingReview(true);
    try {
      const token = getAuthToken();
      await reviewsAPI.createReview({
        bookingId: reviewBooking._id,
        propertyId: reviewBooking.propertyId,
        rating: reviewRating,
        title: reviewTitle.trim(),
        comment: reviewComment.trim()
      }, token);
      toast({ title: 'Review Submitted!', description: 'Thank you for sharing your experience.' });
      setShowReviewModal(false);
      setReviewedBookings(prev => new Set([...prev, reviewBooking._id]));
      setJustReviewedBookings(prev => new Set([...prev, reviewBooking._id]));
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to submit review',
        variant: 'destructive'
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  // Contact Host: create/find conversation then navigate to it
  const handleContactHost = async (booking) => {
    try {
      const token = getAuthToken();
      // Create or find existing conversation for this specific booking
      const conv = await messagesAPI.createConversation({
        propertyId: booking.propertyId,
        bookingId: booking._id
      }, token);
      // Navigate to messages tab with conversation auto-selected
      setSearchParams({ tab: 'messages', conversationId: conv._id }, { replace: false });
    } catch (err) {
      console.error('Error opening conversation:', err);
      toast({ title: 'Opening Messages', description: 'Navigate to your conversation in the Messages tab.' });
      setSearchParams({ tab: 'messages' }, { replace: false });
    }
  };

  // Also update handleRetryPayment to try test payment as fallback
  const handleRetryPaymentOrTest = async (booking) => {
    try {
      const token = getAuthToken();
      try {
        const checkoutData = await paymentsAPI.createCheckoutSession(
          booking._id,
          window.location.origin,
          token
        );
        if (checkoutData.checkoutUrl) {
          window.location.href = checkoutData.checkoutUrl;
          return;
        }
      } catch {
        // Stripe not configured, use test mode
      }
      // Fallback to test payment
      const testResult = await paymentsAPI.testCheckout(booking._id, token);
      if (testResult.success) {
        toast({ title: 'Booking Confirmed!', description: 'Test payment processed. Booking is now confirmed.' });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Failed to process payment',
        variant: 'destructive'
      });
    }
  };

  // Date-aware booking categorisation: bookings whose check-out date has
  // passed are treated as past even if the backend hasn't yet flipped the status.
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  const upcomingBookings = bookings.filter(b => {
    // Cancelled / expired / completed are always "past"
    if (['completed', 'cancelled', 'expired'].includes(b.status)) return false;
    // If check-out date has passed, it belongs in "past" regardless of status
    if (b.checkOut && b.checkOut < today) return false;
    return ['confirmed', 'pending', 'pending_payment'].includes(b.status);
  });

  const pastBookings = bookings.filter(b => {
    if (['completed', 'cancelled', 'expired'].includes(b.status)) return true;
    // Confirmed/pending bookings whose check-out date has passed → treat as past
    if (b.checkOut && b.checkOut < today && ['confirmed', 'pending', 'pending_payment'].includes(b.status)) return true;
    return false;
  });

  // Mock favorites for now - could be stored in localStorage or backend
  const [favorites] = useState([]);

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/guest/browse" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                Browse Properties
              </Link>
              <Link to="/guest/browse?tab=upcoming" className="text-[#1F3A68] font-semibold">
                My Bookings
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <RoleSwitcher />
              <Button variant="outline" size="icon" className="relative">
                <Bell size={20} />
                {unreadMessages > 0 && !messagesAck && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CB3B3B] text-white text-xs rounded-full flex items-center justify-center">
                    {unreadMessages}
                  </span>
                )}
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate('/settings')}>
                <Settings size={20} />
              </Button>
              <Button onClick={handleLogout} className="bg-[#1F3A68] hover:bg-[#1F3A68]/90">
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome Section with Profile Photo */}
        <div className="flex items-center gap-5 mb-8">
          {/* Clickable Profile Photo Avatar */}
          <div className="relative group shrink-0">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-100 flex items-center justify-center">
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User size={32} className="text-gray-300" />
              )}
            </div>
            <label className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" title="Upload profile photo">
              <Camera size={20} className="text-white" />
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => e.target.files?.[0] && handleProfilePhotoUpload(e.target.files[0])}
              />
            </label>
            {uploadingProfilePhoto && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-[#1F3A68] mb-1">{user?.lastLoginAt ? 'Welcome back' : 'Welcome'}, {user?.name?.split(' ')[0] || 'Guest'}!</h1>
            <p className="text-gray-600 text-sm md:text-lg">Manage your trips and discover new places</p>
          </div>
        </div>

        {/* Expand-account prompt — surfaces the multi-role options so
            referral-link signups (which all start as guests) discover
            that the same login can also host, drive, deliver, etc. Only
            shown when the user has fewer than all six partner roles. */}
        {(() => {
          const partnerRoles = ['host', 'landlord', 'driver', 'delivery_rider', 'restaurant_partner', 'service_provider'];
          const missing = partnerRoles.filter(r => !user?.roles?.includes(r));
          if (missing.length === 0) return null;
          return (
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate('/add-role')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/add-role'); }}
              className="mb-6 cursor-pointer bg-gradient-to-r from-[#1F3A68] to-[#2C4F7A] text-white rounded-xl p-5 flex items-center gap-4 hover:shadow-xl transition-shadow"
              data-testid="expand-account-banner"
            >
              <div className="flex-1">
                <p className="text-lg font-bold">Earn with Ariv</p>
                <p className="text-white/80 text-sm leading-snug mt-0.5">
                  Same login. Host a stay, drive a taxi, deliver food, rent out a long let, or offer a service.
                </p>
              </div>
              <div className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
                Add a role →
              </div>
            </div>
          );
        })()}

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/80 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar size={24} />
              </div>
              <div className="text-3xl font-bold mb-1">{upcomingBookings.length}</div>
              <div className="text-white/80">Upcoming Trips</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#CB3B3B] to-[#CB3B3B]/80 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Heart size={24} />
              </div>
              <div className="text-3xl font-bold mb-1">{favorites.length}</div>
              <div className="text-white/80">Saved Properties</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Star size={24} />
              </div>
              <div className="text-3xl font-bold mb-1">{pastBookings.length}</div>
              <div className="text-white/80">Past Stays</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap justify-center w-full bg-white px-3 sm:px-4 py-2 rounded-xl shadow-md gap-1.5">
            <TabsTrigger
              value="browse"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-browse-tab"
            >
              <Search size={14} className="mr-1 hidden sm:inline" />
              Browse
            </TabsTrigger>
            <TabsTrigger
              value="upcoming"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-upcoming-tab"
            >
              Upcoming
            </TabsTrigger>
            <TabsTrigger
              value="past"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-past-tab"
            >
              Past
            </TabsTrigger>
            <TabsTrigger
              value="services"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg relative whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-services-tab"
            >
              Services
              {unreadServiceMessages > 0 && !servicesAck && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CB3B3B] text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {unreadServiceMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg relative whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-messages-tab"
            >
              Messages
              {unreadMessages > 0 && !messagesAck && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CB3B3B] text-white text-xs rounded-full flex items-center justify-center">
                  {unreadMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="refer"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-refer-tab"
            >
              <Gift size={14} className="mr-1 inline" />
              Refer & Earn
            </TabsTrigger>
            <TabsTrigger
              value="faq"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="guest-faq-tab"
            >
              <HelpCircle size={14} className="mr-1 inline" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Browse Properties Tab */}
          <TabsContent value="browse" data-testid="guest-browse-content">
            <GuestBrowse />
          </TabsContent>

          {/* Upcoming Trips Tab */}
          <TabsContent value="upcoming">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68] mx-auto"></div>
              </div>
            ) : upcomingBookings.length === 0 ? (
              <Card className="p-12 text-center">
                <Calendar size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-2xl font-bold text-gray-700 mb-2">No upcoming trips</h3>
                <p className="text-gray-600 mb-6">Time to plan your next adventure!</p>
                <Button onClick={() => setActiveTab('browse')} className="bg-[#1F3A68] hover:bg-[#1F3A68]/90">
                  Browse Properties
                </Button>
              </Card>
            ) : (
              <div className="space-y-6">
                {upcomingBookings.map(booking => (
                  <Card key={booking._id} className="overflow-hidden hover:shadow-xl transition-all">
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="relative">
                        <img
                          src={booking.propertyImage || 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800'}
                          alt={booking.propertyTitle}
                          className="w-full h-full object-cover md:h-64"
                        />
                        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 ${
                          booking.status === 'confirmed' ? 'bg-green-500 text-white' :
                          booking.status === 'pending_payment' ? 'bg-orange-500 text-white' :
                          'bg-amber-500 text-white'
                        }`}>
                          {booking.status === 'confirmed' ? <Check size={14} /> : 
                           booking.status === 'pending_payment' ? <CreditCard size={14} /> :
                           <Clock size={14} />}
                          {booking.status === 'confirmed' ? 'Confirmed' : 
                           booking.status === 'pending_payment' ? 'Awaiting Payment' :
                           'Pending'}
                        </div>
                      </div>

                      <div className="md:col-span-2 p-4 md:p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2 line-clamp-2">{booking.propertyTitle}</h3>
                            <p className="text-gray-600 flex items-center gap-1 mb-2">
                              <MapPin size={16} />
                              {booking.propertyLocation}
                            </p>
                            <p className="text-gray-600 text-sm">Hosted by {booking.hostName}</p>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Check-in</div>
                            <div className="font-semibold text-lg">{booking.checkIn}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Check-out</div>
                            <div className="font-semibold text-lg">{booking.checkOut}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Guests</div>
                            <div className="font-semibold text-lg flex items-center gap-1">
                              <Users size={18} />
                              {booking.numberOfGuests}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Total</div>
                            <div className="font-semibold text-lg text-[#1F3A68]">
                              £{booking.pricing?.total?.toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {/* Payment deadline banner for reserve-later bookings */}
                        {booking.status === 'pending_payment' && booking.paymentDeadline && (
                          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                            <Clock size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-semibold text-amber-800">Payment Required</p>
                              <p className="text-amber-700">
                                Complete payment by{' '}
                                <span className="font-bold">
                                  {new Date(booking.paymentDeadline).toLocaleDateString('en-GB', {
                                    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  })}
                                </span>
                                {' '}or your reservation will expire and dates will become available again.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Departure photo advisory for confirmed bookings */}
                        {booking.status === 'confirmed' && (
                          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                            <Camera size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-semibold text-blue-800">Protect yourself — take photos when leaving</p>
                              <p className="text-blue-700">
                                We recommend taking timestamped photos of the property before you leave. This protects you against any false damage claims.
                                Use the camera in your messages with the host to capture evidence photos.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3 flex-wrap">
                          {booking.status === 'pending_payment' && (
                            <Button
                              className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                              onClick={() => handleRetryPaymentOrTest(booking)}
                              data-testid={`pay-now-${booking._id}`}
                            >
                              <CreditCard size={16} className="mr-1" />
                              Pay Now
                            </Button>
                          )}
                          <Button
                            className={booking.status === 'pending_payment' ? '' : 'bg-[#1F3A68] hover:bg-[#1F3A68]/90'}
                            variant={booking.status === 'pending_payment' ? 'outline' : 'default'}
                            onClick={() => navigate(`/property/${booking.propertyId}`)}
                          >
                            View Details
                          </Button>
                          <Button variant="outline" onClick={() => handleContactHost(booking)}>
                            <MessageSquare size={16} className="mr-1" />
                            Contact Host
                          </Button>
                          {booking.status === 'confirmed' ? (
                            <Button 
                              variant="outline"
                              onClick={() => generateInvoicePDF(booking)}
                              data-testid={`download-invoice-${booking._id}`}
                            >
                              <FileText size={16} className="mr-1" />
                              Download Invoice
                            </Button>
                          ) : (
                            <Button 
                              variant="outline"
                              disabled
                              title="Invoice available after payment"
                            >
                              <FileText size={16} className="mr-1" />
                              Invoice Pending
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleCancelBooking(booking._id, booking.status === 'pending_payment')}
                          >
                            <X size={16} className="mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Past Trips Tab */}
          <TabsContent value="past">
            {pastBookings.length === 0 ? (
              <Card className="p-12 text-center">
                <Clock size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-2xl font-bold text-gray-700 mb-2">No past trips</h3>
                <p className="text-gray-600">Your completed trips will appear here</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {pastBookings.map(booking => (
                  <Card key={booking._id} className="overflow-hidden">
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="relative">
                        <img
                          src={booking.propertyImage || 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800'}
                          alt={booking.propertyTitle}
                          className="w-full h-full object-cover md:h-64 grayscale-[30%]"
                        />
                        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-semibold ${
                          (booking.status === 'completed' || (booking.checkOut && booking.checkOut < today && booking.status === 'confirmed'))
                            ? 'bg-gray-500 text-white'
                            : booking.status === 'expired' ? 'bg-gray-400 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          {(booking.status === 'completed' || (booking.checkOut && booking.checkOut < today && booking.status === 'confirmed'))
                            ? 'Completed'
                            : booking.status === 'expired' ? 'Expired'
                            : 'Cancelled'}
                        </div>
                      </div>

                      <div className="md:col-span-2 p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{booking.propertyTitle}</h3>
                        <p className="text-gray-600 flex items-center gap-1 mb-4">
                          <MapPin size={16} />
                          {booking.propertyLocation}
                        </p>

                        <div className="flex items-center gap-1 mb-4">
                          <Clock size={16} className="text-gray-500" />
                          <span className="text-gray-600">
                            {booking.checkIn} to {booking.checkOut}
                          </span>
                        </div>

                        {booking.status === 'expired' && (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                            <p className="text-gray-600 text-sm">
                              This reservation expired because payment was not received in time.
                              The dates are now available for other guests.
                            </p>
                          </div>
                        )}

                        {(booking.status === 'completed' || (booking.checkOut && booking.checkOut < today && booking.status === 'confirmed')) && !reviewedBookings.has(booking._id) && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                            <p className="text-amber-800 font-semibold mb-2">Share your experience</p>
                            <p className="text-amber-700 text-sm mb-3">
                              Help other travelers by leaving a review
                            </p>
                            <Button
                              className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                              onClick={() => openReviewModal(booking)}
                            >
                              <Star size={16} className="mr-1" />
                              Write a Review
                            </Button>
                          </div>
                        )}
                        {justReviewedBookings.has(booking._id) && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            <span className="text-green-700 text-sm font-medium">Review submitted — thank you!</span>
                          </div>
                        )}

                        <div className="flex gap-3">
                          <Button variant="outline" onClick={() => navigate(`/property/${booking.propertyId}`)}>
                            View Property
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/property/${booking.propertyId}`)}
                          >
                            Book Again
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Services Tab (includes Food & Taxi) */}
          <TabsContent value="services">
            <Tabs value={servicesSubTab} onValueChange={setServicesSubTab}>
              <TabsList className="flex w-full bg-gray-100 px-2 sm:px-3 py-1.5 rounded-lg mb-4 gap-1 sm:gap-2">
                <TabsTrigger value="food" className="flex-1 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="guest-food-tab">
                  <UtensilsCrossed size={14} className="mr-1" />
                  Food
                </TabsTrigger>
                <TabsTrigger value="taxi" className="flex-1 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="guest-taxi-tab">
                  <Car size={14} className="mr-1" />
                  Taxi
                </TabsTrigger>
                <TabsTrigger value="service-messages" className="flex-1 data-[state=active]:bg-white data-[state=active]:shadow-sm relative" data-testid="guest-service-messages-tab">
                  <MessageSquare size={14} className="mr-1" />
                  Messages
                  {unreadServiceMessages > 0 && !servicesAck && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#CB3B3B] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {unreadServiceMessages}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="food" data-testid="guest-food-content">
                <FoodOrdering token={getAuthToken()} user={user} activeBooking={bookings.find(b => b.status === 'confirmed')} />
              </TabsContent>
              <TabsContent value="taxi" data-testid="guest-taxi-content">
                <RideBooking booking={bookings.find(b => b.status === 'confirmed')} onNavigateToMessages={(convId) => {
                  if (convId) {
                    setSearchParams({ tab: 'messages', conversationId: convId }, { replace: false });
                  } else {
                    setSearchParams({ tab: 'messages' }, { replace: false });
                  }
                }} />
              </TabsContent>
              <TabsContent value="service-messages" data-testid="guest-service-messages-content">
                <Card>
                  <CardContent className="p-4">
                    <ServiceMessaging
                      openConversationId={openConversationId}
                      openConversationData={openConversationData}
                      onConversationOpened={() => { setOpenConversationId(null); setOpenConversationData(null); }}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1F3A68]">Messages</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <MessagingPanel
                  userRole="guest"
                  autoSelectConversationId={searchParams.get('conversationId')}
                  onUnreadChange={handleUnreadChange}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="refer">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="faq">
            <FAQFeedback />
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-[#1F3A68]">Write a Review</DialogTitle>
            <DialogDescription>
              Share your experience at {reviewBooking?.propertyTitle}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Star Rating */}
            <div>
              <label className="block text-sm font-medium mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star
                      size={28}
                      className={star <= reviewRating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}
                    />
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-500 self-center">
                  {reviewRating === 5 ? 'Excellent' : reviewRating === 4 ? 'Great' : reviewRating === 3 ? 'Good' : reviewRating === 2 ? 'Fair' : 'Poor'}
                </span>
              </div>
            </div>

            {/* Review Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Title (optional)</label>
              <Input
                placeholder="Summarize your stay..."
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
              />
            </div>

            {/* Review Comment */}
            <div>
              <label className="block text-sm font-medium mb-1">Your Review</label>
              <Textarea
                placeholder="Tell us about your experience — the property, the host, the location..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={submittingReview || !reviewComment.trim()}
              className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Preview Modal — shown when guest clicks Cancel on a
          paid booking. The numbers come from the backend's refund-preview
          endpoint, which evaluates the booking's snapshotted cancellation
          policy against the current time. On submit the backend re-runs
          the same calculation and refuses with 409 if the number has
          changed (threshold crossed while the modal was open), in which
          case we silently refresh the preview and re-prompt. */}
      <Dialog
        open={!!cancelPreview}
        onOpenChange={(open) => { if (!open && !cancelSubmitting) setCancelPreview(null); }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-[#1F3A68] text-xl">Cancel this booking?</DialogTitle>
            <DialogDescription>
              {cancelPreview?.booking?.propertyTitle} · {cancelPreview?.booking?.checkIn} to {cancelPreview?.booking?.checkOut}
            </DialogDescription>
          </DialogHeader>

          {cancelPreview && (
            <div className="py-4 space-y-4">
              {/* Policy headline */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-[#1F3A68]">
                  {cancelPreview.preview?.policySummary?.label || 'Cancellation policy'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {cancelPreview.preview?.policySummary?.headline || ''}
                </p>
              </div>

              {/* Plain-English explanation specific to this cancellation */}
              {cancelPreview.preview?.breakdown?.explanation && (
                <p className="text-sm text-gray-700">
                  {cancelPreview.preview.breakdown.explanation}
                </p>
              )}

              {/* The numbers */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">You paid</span>
                  <span>£{Number(cancelPreview.booking?.pricing?.total || 0).toFixed(2)}</span>
                </div>
                {Number(cancelPreview.preview?.breakdown?.accommodation_refund || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Accommodation refund</span>
                    <span>£{Number(cancelPreview.preview.breakdown.accommodation_refund).toFixed(2)}</span>
                  </div>
                )}
                {Number(cancelPreview.preview?.breakdown?.cleaning_refund || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cleaning fee refund</span>
                    <span>£{Number(cancelPreview.preview.breakdown.cleaning_refund).toFixed(2)}</span>
                  </div>
                )}
                {Number(cancelPreview.preview?.breakdown?.service_fee_refund || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service fee refund</span>
                    <span>£{Number(cancelPreview.preview.breakdown.service_fee_refund).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base border-t pt-2 text-[#1F3A68]">
                  <span>Refund to you</span>
                  <span>£{Number(cancelPreview.preview?.breakdown?.guest_refund || 0).toFixed(2)}</span>
                </div>
                <p className="text-xs text-gray-500 pt-1">
                  Refunds typically clear to your card in 5-10 business days.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelPreview(null)}
              disabled={cancelSubmitting}
            >
              Keep Booking
            </Button>
            <Button
              onClick={submitCancellation}
              disabled={cancelSubmitting}
              className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
            >
              {cancelSubmitting ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestDashboard;
